export interface RoutineBlock {
  id: string
  user_id?: string
  name: string
  description?: string
  time_range: 'morning' | 'afternoon' | 'evening' | 'anytime'
  is_active: boolean
  order_index: number
  tasks: RoutineTask[]
  created_at: Date
  updated_at: Date
  reminder_enabled?: boolean
  reminder_time?: string // Format: "HH:MM"
  reminder_window?: number // Minutes of flexibility
}

export interface RoutineTask {
  id: string
  block_id: string
  task_name: string
  description?: string
  estimated_minutes: number
  is_essential: boolean // for low-energy mode
  order_index: number
  steps: RoutineStep[]
  created_at: Date
  updated_at: Date
}

export interface RoutineStep {
  id: string
  task_id: string
  step_text: string
  order_index: number
  is_optional: boolean
  created_at: Date
}

export interface TaskProgress {
  id: string
  user_id: string
  task_id: string
  block_id: string
  completed_at?: Date
  skipped_at?: Date
  completion_time_seconds?: number
  mood_before?: 'low' | 'okay' | 'good'
  mood_after?: 'low' | 'okay' | 'good'
  notes?: string
  session_date: string
  created_at: Date
  skipped_steps?: string[] // IDs of steps that were skipped
}

export interface RoutineStreak {
  id: string
  user_id: string
  block_id: string
  current_streak: number
  longest_streak: number
  last_completed_date?: string
  total_completions: number
  created_at: Date
  updated_at: Date
}

export interface RoutineReminder {
  id: string
  user_id: string
  block_id: string
  enabled: boolean
  time: string // "HH:MM" format
  window_minutes: number // Flexibility window
  skip_count: number // How many times user has skipped
  last_shown: Date | null
  created_at: Date
  updated_at: Date
}

const ROUTINES_STORAGE_KEY = 'user_routines'
const PROGRESS_STORAGE_KEY = 'routine_progress'
const STREAKS_STORAGE_KEY = 'routine_streaks'
const REMINDERS_STORAGE_KEY = 'routine_reminders'

// Default routine templates
export const getDefaultRoutines = (): RoutineBlock[] => [
  {
    id: 'morning-reset',
    name: 'Morning Reset',
    description: 'A gentle way to start your day with intention',
    time_range: 'morning',
    is_active: true,
    order_index: 0,
    reminder_enabled: false,
    tasks: [
      {
        id: 'morning-breathing',
        block_id: 'morning-reset',
        task_name: 'Breathing Exercise',
        description: 'Center yourself with gentle breathing',
        estimated_minutes: 3,
        is_essential: true,
        order_index: 0,
        steps: [
          {
            id: 'breath-1',
            task_id: 'morning-breathing',
            step_text: 'Find a comfortable seated position',
            order_index: 0,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'breath-2',
            task_id: 'morning-breathing',
            step_text: 'Place one hand on your chest, one on your belly',
            order_index: 1,
            is_optional: true,
            created_at: new Date()
          },
          {
            id: 'breath-3',
            task_id: 'morning-breathing',
            step_text: 'Inhale slowly for 4 counts',
            order_index: 2,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'breath-4',
            task_id: 'morning-breathing',
            step_text: 'Hold gently for 3 counts',
            order_index: 3,
            is_optional: true,
            created_at: new Date()
          },
          {
            id: 'breath-5',
            task_id: 'morning-breathing',
            step_text: 'Exhale slowly for 5 counts',
            order_index: 4,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'breath-6',
            task_id: 'morning-breathing',
            step_text: 'Repeat 5-10 times at your own pace',
            order_index: 5,
            is_optional: true,
            created_at: new Date()
          }
        ],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 'morning-intention',
        block_id: 'morning-reset',
        task_name: 'Set Daily Intention',
        description: 'Choose one gentle focus for today',
        estimated_minutes: 2,
        is_essential: true,
        order_index: 1,
        steps: [
          {
            id: 'intention-1',
            task_id: 'morning-intention',
            step_text: 'Take a moment to notice how you feel',
            order_index: 0,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'intention-2',
            task_id: 'morning-intention',
            step_text: 'Think of one word that feels supportive today',
            order_index: 1,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'intention-3',
            task_id: 'morning-intention',
            step_text: 'Silently repeat this word to yourself',
            order_index: 2,
            is_optional: true,
            created_at: new Date()
          }
        ],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 'morning-gratitude',
        block_id: 'morning-reset',
        task_name: 'Quick Gratitude',
        description: 'Notice something small that feels okay',
        estimated_minutes: 1,
        is_essential: false,
        order_index: 2,
        steps: [
          {
            id: 'gratitude-1',
            task_id: 'morning-gratitude',
            step_text: 'Look around and notice one thing that feels peaceful',
            order_index: 0,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'gratitude-2',
            task_id: 'morning-gratitude',
            step_text: 'Say "thank you" quietly to yourself',
            order_index: 1,
            is_optional: true,
            created_at: new Date()
          }
        ],
        created_at: new Date(),
        updated_at: new Date()
      }
    ],
    created_at: new Date(),
    updated_at: new Date()
  },
  {
    id: 'evening-wind-down',
    name: 'Evening Wind Down',
    description: 'Gentle transition into rest',
    time_range: 'evening',
    is_active: true,
    order_index: 1,
    reminder_enabled: false,
    tasks: [
      {
        id: 'evening-reflection',
        block_id: 'evening-wind-down',
        task_name: 'Day Reflection',
        description: 'Acknowledge your day with kindness',
        estimated_minutes: 3,
        is_essential: true,
        order_index: 0,
        steps: [
          {
            id: 'reflect-1',
            task_id: 'evening-reflection',
            step_text: 'Think of one thing that went okay today',
            order_index: 0,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'reflect-2',
            task_id: 'evening-reflection',
            step_text: 'Notice one way you cared for yourself',
            order_index: 1,
            is_optional: true,
            created_at: new Date()
          },
          {
            id: 'reflect-3',
            task_id: 'evening-reflection',
            step_text: 'Forgive yourself for anything that felt hard',
            order_index: 2,
            is_optional: true,
            created_at: new Date()
          }
        ],
        created_at: new Date(),
        updated_at: new Date()
      },
      {
        id: 'evening-release',
        block_id: 'evening-wind-down',
        task_name: 'Release the Day',
        description: 'Let go of what you carried',
        estimated_minutes: 2,
        is_essential: false,
        order_index: 1,
        steps: [
          {
            id: 'release-1',
            task_id: 'evening-release',
            step_text: 'Take three deep breaths',
            order_index: 0,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'release-2',
            task_id: 'evening-release',
            step_text: 'With each exhale, let go of tension',
            order_index: 1,
            is_optional: false,
            created_at: new Date()
          },
          {
            id: 'release-3',
            task_id: 'evening-release',
            step_text: 'Say "I did my best today" to yourself',
            order_index: 2,
            is_optional: true,
            created_at: new Date()
          }
        ],
        created_at: new Date(),
        updated_at: new Date()
      }
    ],
    created_at: new Date(),
    updated_at: new Date()
  }
]

// Local storage functions
export const getUserRoutines = (): RoutineBlock[] => {
  try {
    const stored = localStorage.getItem(ROUTINES_STORAGE_KEY)
    if (!stored) {
      const defaultRoutines = getDefaultRoutines()
      saveUserRoutines(defaultRoutines)
      return defaultRoutines
    }
    
    const routines = JSON.parse(stored)
    return routines.map((routine: any) => ({
      ...routine,
      created_at: new Date(routine.created_at),
      updated_at: new Date(routine.updated_at),
      tasks: routine.tasks.map((task: any) => ({
        ...task,
        created_at: new Date(task.created_at),
        updated_at: new Date(task.updated_at),
        steps: task.steps.map((step: any) => ({
          ...step,
          created_at: new Date(step.created_at)
        }))
      }))
    }))
  } catch (error) {
    console.error('Error loading routines:', error)
    return getDefaultRoutines()
  }
}

export const saveUserRoutines = (routines: RoutineBlock[]): void => {
  try {
    localStorage.setItem(ROUTINES_STORAGE_KEY, JSON.stringify(routines))
  } catch (error) {
    console.error('Error saving routines:', error)
  }
}

export const getTaskProgress = (): TaskProgress[] => {
  try {
    const stored = localStorage.getItem(PROGRESS_STORAGE_KEY)
    if (!stored) return []
    
    const progress = JSON.parse(stored)
    return progress.map((p: any) => ({
      ...p,
      completed_at: p.completed_at ? new Date(p.completed_at) : undefined,
      skipped_at: p.skipped_at ? new Date(p.skipped_at) : undefined,
      created_at: new Date(p.created_at)
    }))
  } catch (error) {
    console.error('Error loading task progress:', error)
    return []
  }
}

export const saveTaskProgress = (progress: TaskProgress[]): void => {
  try {
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(progress))
  } catch (error) {
    console.error('Error saving task progress:', error)
  }
}

export const recordTaskCompletion = (
  taskId: string,
  blockId: string,
  completionTimeSeconds?: number,
  moodBefore?: 'low' | 'okay' | 'good',
  moodAfter?: 'low' | 'okay' | 'good',
  notes?: string,
  skippedSteps?: string[]
): TaskProgress => {
  const progress: TaskProgress = {
    id: crypto.randomUUID(),
    user_id: 'local-user', // Will be updated when synced to Supabase
    task_id: taskId,
    block_id: blockId,
    completed_at: new Date(),
    completion_time_seconds: completionTimeSeconds,
    mood_before: moodBefore,
    mood_after: moodAfter,
    notes,
    skipped_steps: skippedSteps,
    session_date: new Date().toLocaleDateString('en-CA'), // YYYY-MM-DD format
    created_at: new Date()
  }

  const allProgress = getTaskProgress()
  allProgress.unshift(progress)
  saveTaskProgress(allProgress)
  
  // Update streaks
  updateStreakForBlock(blockId)
  
  return progress
}

export const recordTaskSkip = (taskId: string, blockId: string, notes?: string): TaskProgress => {
  const progress: TaskProgress = {
    id: crypto.randomUUID(),
    user_id: 'local-user',
    task_id: taskId,
    block_id: blockId,
    skipped_at: new Date(),
    notes,
    session_date: new Date().toLocaleDateString('en-CA'),
    created_at: new Date()
  }

  const allProgress = getTaskProgress()
  allProgress.unshift(progress)
  saveTaskProgress(allProgress)
  
  return progress
}

// Reminder system
export const getRoutineReminders = (): RoutineReminder[] => {
  try {
    const stored = localStorage.getItem(REMINDERS_STORAGE_KEY)
    if (!stored) return []
    
    const reminders = JSON.parse(stored)
    return reminders.map((r: any) => ({
      ...r,
      last_shown: r.last_shown ? new Date(r.last_shown) : null,
      created_at: new Date(r.created_at),
      updated_at: new Date(r.updated_at)
    }))
  } catch (error) {
    console.error('Error loading reminders:', error)
    return []
  }
}

export const saveRoutineReminders = (reminders: RoutineReminder[]): void => {
  try {
    localStorage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify(reminders))
  } catch (error) {
    console.error('Error saving reminders:', error)
  }
}

export const updateReminderSettings = (
  blockId: string, 
  enabled: boolean, 
  time?: string, 
  windowMinutes?: number
): void => {
  const reminders = getRoutineReminders()
  const existingIndex = reminders.findIndex(r => r.block_id === blockId)
  
  if (existingIndex >= 0) {
    reminders[existingIndex] = {
      ...reminders[existingIndex],
      enabled,
      time: time || reminders[existingIndex].time,
      window_minutes: windowMinutes || reminders[existingIndex].window_minutes,
      updated_at: new Date()
    }
  } else {
    const newReminder: RoutineReminder = {
      id: crypto.randomUUID(),
      user_id: 'local-user',
      block_id: blockId,
      enabled,
      time: time || '09:00',
      window_minutes: windowMinutes || 60,
      skip_count: 0,
      last_shown: null,
      created_at: new Date(),
      updated_at: new Date()
    }
    reminders.push(newReminder)
  }
  
  saveRoutineReminders(reminders)
}

export const checkForReminders = (): RoutineReminder[] => {
  const reminders = getRoutineReminders()
  const now = new Date()
  const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
  
  return reminders.filter(reminder => {
    if (!reminder.enabled) return false
    
    // Check if it's within the time window
    const [targetHour, targetMinute] = reminder.time.split(':').map(Number)
    const targetTime = targetHour * 60 + targetMinute
    const currentTimeMinutes = now.getHours() * 60 + now.getMinutes()
    
    const timeDiff = Math.abs(currentTimeMinutes - targetTime)
    const isInWindow = timeDiff <= reminder.window_minutes
    
    if (!isInWindow) return false
    
    // Check if we've already shown this reminder today
    if (reminder.last_shown) {
      const lastShownDate = new Date(reminder.last_shown).toDateString()
      const todayDate = now.toDateString()
      if (lastShownDate === todayDate) return false
    }
    
    // Check if user has skipped too many times (pause after 3 skips)
    if (reminder.skip_count >= 3) return false
    
    return true
  })
}

export const markReminderShown = (reminderId: string): void => {
  const reminders = getRoutineReminders()
  const reminderIndex = reminders.findIndex(r => r.id === reminderId)
  
  if (reminderIndex >= 0) {
    reminders[reminderIndex] = {
      ...reminders[reminderIndex],
      last_shown: new Date(),
      updated_at: new Date()
    }
    saveRoutineReminders(reminders)
  }
}

export const markReminderSkipped = (reminderId: string): void => {
  const reminders = getRoutineReminders()
  const reminderIndex = reminders.findIndex(r => r.id === reminderId)
  
  if (reminderIndex >= 0) {
    reminders[reminderIndex] = {
      ...reminders[reminderIndex],
      skip_count: reminders[reminderIndex].skip_count + 1,
      last_shown: new Date(),
      updated_at: new Date()
    }
    saveRoutineReminders(reminders)
  }
}

export const resetReminderSkipCount = (reminderId: string): void => {
  const reminders = getRoutineReminders()
  const reminderIndex = reminders.findIndex(r => r.id === reminderId)
  
  if (reminderIndex >= 0) {
    reminders[reminderIndex] = {
      ...reminders[reminderIndex],
      skip_count: 0,
      updated_at: new Date()
    }
    saveRoutineReminders(reminders)
  }
}

// Streak management
export const getRoutineStreaks = (): RoutineStreak[] => {
  try {
    const stored = localStorage.getItem(STREAKS_STORAGE_KEY)
    if (!stored) return []
    
    const streaks = JSON.parse(stored)
    return streaks.map((s: any) => ({
      ...s,
      created_at: new Date(s.created_at),
      updated_at: new Date(s.updated_at)
    }))
  } catch (error) {
    console.error('Error loading streaks:', error)
    return []
  }
}

export const saveRoutineStreaks = (streaks: RoutineStreak[]): void => {
  try {
    localStorage.setItem(STREAKS_STORAGE_KEY, JSON.stringify(streaks))
  } catch (error) {
    console.error('Error saving streaks:', error)
  }
}

export const updateStreakForBlock = (blockId: string): void => {
  const streaks = getRoutineStreaks()
  const today = new Date().toLocaleDateString('en-CA')
  
  let blockStreak = streaks.find(s => s.block_id === blockId)
  
  if (!blockStreak) {
    blockStreak = {
      id: crypto.randomUUID(),
      user_id: 'local-user',
      block_id: blockId,
      current_streak: 1,
      longest_streak: 1,
      last_completed_date: today,
      total_completions: 1,
      created_at: new Date(),
      updated_at: new Date()
    }
    streaks.push(blockStreak)
  } else {
    // Check if this is a consecutive day
    const lastDate = new Date(blockStreak.last_completed_date || '')
    const todayDate = new Date(today)
    const daysDiff = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24))
    
    if (daysDiff === 1) {
      // Consecutive day
      blockStreak.current_streak += 1
      blockStreak.longest_streak = Math.max(blockStreak.longest_streak, blockStreak.current_streak)
    } else if (daysDiff > 1) {
      // Streak broken
      blockStreak.current_streak = 1
    }
    // If daysDiff === 0, it's the same day, don't change streak
    
    blockStreak.last_completed_date = today
    blockStreak.total_completions += 1
    blockStreak.updated_at = new Date()
  }
  
  saveRoutineStreaks(streaks)
}

export const getStreakForBlock = (blockId: string): RoutineStreak | null => {
  const streaks = getRoutineStreaks()
  return streaks.find(s => s.block_id === blockId) || null
}

// Analytics and insights
export const getRoutineInsights = () => {
  const progress = getTaskProgress()
  const streaks = getRoutineStreaks()
  const routines = getUserRoutines()
  
  const today = new Date().toLocaleDateString('en-CA')
  const thisWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA')
  
  // Today's completions
  const todayCompletions = progress.filter(p => 
    p.session_date === today && p.completed_at
  ).length
  
  // This week's completions
  const weekCompletions = progress.filter(p => 
    p.session_date >= thisWeek && p.completed_at
  ).length
  
  // Current streaks
  const activeStreaks = streaks.filter(s => s.current_streak > 0)
  
  // Most consistent routine
  const mostConsistent = streaks.reduce((best, current) => 
    current.longest_streak > (best?.longest_streak || 0) ? current : best
  , null as RoutineStreak | null)
  
  return {
    todayCompletions,
    weekCompletions,
    activeStreaks: activeStreaks.length,
    longestStreak: mostConsistent?.longest_streak || 0,
    totalCompletions: progress.filter(p => p.completed_at).length,
    favoriteTimeOfDay: getMostActiveTimeOfDay(progress, routines)
  }
}

const getMostActiveTimeOfDay = (progress: TaskProgress[], routines: RoutineBlock[]): string => {
  const timeRangeCompletions = { morning: 0, afternoon: 0, evening: 0, anytime: 0 }
  
  progress.forEach(p => {
    if (p.completed_at) {
      const routine = routines.find(r => r.id === p.block_id)
      if (routine) {
        timeRangeCompletions[routine.time_range]++
      }
    }
  })
  
  return Object.entries(timeRangeCompletions).reduce((a, b) => 
    timeRangeCompletions[a[0] as keyof typeof timeRangeCompletions] > 
    timeRangeCompletions[b[0] as keyof typeof timeRangeCompletions] ? a : b
  )[0]
}